<script setup lang="ts">
import RewardSelector from '@/components/ui/RewardSelector.vue'
import { getRewardTypeLabel } from '@/components/rewards/utils/rewardHelpers'

import type { LoyaltyReward } from '@/types/types'

const { t } = useI18n()

const props = defineProps<{
  selectedReward: LoyaltyReward | null
  rewards: LoyaltyReward[]
  readonly?: boolean
}>()

const emit = defineEmits<{
  (e: 'open-offer-reward-modal'): void
  (e: 'open-offer-reward-selection-modal'): void
}>()

const offerName = defineModel<string>('offerName', { required: true })
const internalDescription = defineModel<string>('internalDescription', { required: true })
const selectedRewardId = defineModel<number | null>('selectedRewardId')

const NAME_MAX_LENGTH = 50
const DESCRIPTION_MAX_LENGTH = 145

const isNameAtLimit = computed(() => offerName.value.length >= NAME_MAX_LENGTH)
const isDescriptionAtLimit = computed(() => internalDescription.value.length >= DESCRIPTION_MAX_LENGTH)

watch(
  offerName,
  (value) => {
    if (value && value.length > NAME_MAX_LENGTH) {
      offerName.value = value.slice(0, NAME_MAX_LENGTH)
    }
  },
  { immediate: true }
)

watch(
  internalDescription,
  (value) => {
    if (value && value.length > DESCRIPTION_MAX_LENGTH) {
      internalDescription.value = value.slice(0, DESCRIPTION_MAX_LENGTH)
    }
  },
  { immediate: true }
)

const rewardTypeLabel = computed(() =>
  props.selectedReward?.rewardType ? getRewardTypeLabel(props.selectedReward.rewardType, t) : ''
)
</script>

<template>
  <div class="flex flex-col gap-5">
    <Card>
      <CardHeader>
        <CardTitle>{{ t('pages.offer.create.form.details.title') }}</CardTitle>
      </CardHeader>
      <CardContent class="space-y-5">
        <div>
          <label for="offer-name" class="mb-1.5 block text-sm font-medium text-foreground">
            {{ t('pages.offer.create.form.details.nameLabel') }}
            <span v-if="!readonly" class="text-red-400">*</span>
          </label>
          <Input
            id="offer-name"
            v-model="offerName"
            :maxlength="NAME_MAX_LENGTH"
            :aria-invalid="isNameAtLimit || undefined"
            :placeholder="t('pages.offer.create.form.details.namePlaceholder')"
            class="w-full"
            :disabled="readonly"
          />
          <p class="mt-1 text-xs" :class="isNameAtLimit ? 'text-red-500' : 'text-muted-foreground'">
            {{ offerName.length }}/{{ NAME_MAX_LENGTH }}
          </p>
        </div>
        <div>
          <label for="offer-description" class="mb-1.5 block text-sm font-medium text-foreground">
            {{ t('pages.offer.create.form.details.descriptionLabel') }}
          </label>
          <Textarea
            id="offer-description"
            v-model="internalDescription"
            :placeholder="t('pages.offer.create.form.details.descriptionPlaceholder')"
            :rows="5"
            :maxlength="DESCRIPTION_MAX_LENGTH"
            class="min-h-[135px] w-full text-sm"
            :readonly="readonly"
          />
          <p class="mt-1 text-xs" :class="isDescriptionAtLimit ? 'text-red-500' : 'text-muted-foreground'">
            {{ internalDescription.length }}/{{ DESCRIPTION_MAX_LENGTH }}
          </p>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>
          {{ t('pages.offer.create.form.benefit.title') }}
          <span v-if="!readonly" class="text-red-400">*</span>
        </CardTitle>
        <CardDescription>{{ t('pages.offer.create.form.benefit.subtitle') }}</CardDescription>
      </CardHeader>

      <CardContent>
        <div v-if="readonly">
          <div v-if="selectedReward" class="w-full rounded-xl border border-border bg-white px-4 py-3">
            <div class="flex items-center gap-3">
              <img
                v-if="selectedReward.pictureUrl"
                :src="selectedReward.pictureUrl"
                :alt="selectedReward.name"
                class="h-12 w-12 rounded-lg object-cover"
              />
              <div v-else class="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <BaseIcon name="HeroGiftIconOutline" class="h-6 w-6 text-primary" />
              </div>
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold text-foreground">
                  {{ selectedReward.name }}
                </p>
                <p v-if="rewardTypeLabel" class="truncate text-xs text-muted-foreground">
                  {{ rewardTypeLabel }}
                </p>
              </div>
            </div>
          </div>
          <div
            v-else
            class="flex min-h-[84px] w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/40 text-center text-sm text-muted-foreground"
          >
            <BaseIcon name="HeroGiftIconOutline" class="h-5 w-5" />
            <span>
              {{ t('pages.offer.create.form.benefit.addButton') }}
            </span>
          </div>
        </div>
        <div v-else>
          <RewardSelector
            v-model:selected-reward-id="selectedRewardId"
            :rewards="rewards"
            :show-points="true"
            use-external-change-handler
            selection-usage="offer"
            @change-reward="emit('open-offer-reward-selection-modal')"
            @edit-reward="emit('open-offer-reward-modal')"
          />
        </div>
      </CardContent>
    </Card>
  </div>
</template>
